import React, { useState } from 'react';
import { FileText, Download, Eye, Search, Filter, ArrowDownCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

// Sample resources data
const resources = [
  { 
    id: 1, 
    title: 'Sustainable Farming Techniques', 
    size: '2.5 MB', 
    type: 'PDF', 
    category: 'Agriculture',
    description: 'A guide to eco-friendly and productive farming.', 
    file: '/pdf/project01.pdf' 
  },
  { 
    id: 2, 
    title: 'Family Nutrition Guide', 
    size: '1.8 MB', 
    type: 'PDF', 
    category: 'Health',
    description: 'Learn about balanced diets for all ages.', 
    file: '/pdf/project02.pdf' 
  },
  { 
    id: 3, 
    title: 'Household Budgeting Planner', 
    size: '800 KB', 
    type: 'PDF', 
    category: 'Finance',
    description: 'A simple planner to manage your finances.', 
    file: '/pdf/project01.pdf' 
  },
  { 
    id: 4, 
    title: 'Water Conservation Methods', 
    size: '3.1 MB', 
    type: 'PDF', 
    category: 'Environment',
    description: 'Techniques to save and manage water resources.', 
    file: '/pdf/project02.pdf' 
  },
  { 
    id: 5, 
    title: 'Basic First Aid Handbook', 
    size: '2.2 MB', 
    type: 'PDF', 
    category: 'Health',
    description: 'Essential first aid procedures for common injuries.', 
    file: '/pdf/project01.pdf' 
  },
  { 
    id: 6, 
    title: 'Organic Gardening Guide', 
    size: '3.4 MB', 
    type: 'PDF', 
    category: 'Agriculture',
    description: 'How to grow plants without synthetic pesticides.', 
    file: '/pdf/project02.pdf' 
  },
];

// Function to download PDF
const downloadPDF = (filePath, fileName) => {
  // Create a temporary anchor tag
  const link = document.createElement('a');
  link.href = filePath;
  link.download = fileName || filePath.split('/').pop();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function OfflineResourcesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [downloading, setDownloading] = useState(null);

  // Get unique categories for filter
  const categories = ['All', ...new Set(resources.map(resource => resource.category))];

  // Filter resources based on search and category
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Handle download with visual feedback
  const handleDownload = (resource) => {
    setDownloading(resource.id);
    
    // Simulate download process
    setTimeout(() => {
      downloadPDF(resource.file, `${resource.title}.pdf`);
      setDownloading(null);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-300 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-800 mb-3 flex items-center justify-center gap-2">
            <FileText className="w-10 h-10 text-blue-600" />
            Offline Resources
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Downloadable guides for you to access anytime, anywhere, even without internet connection.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search resources..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 items-center">
              <Filter className="text-gray-500 w-5 h-5" />
              <select
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredResources.length > 0 ? (
            filteredResources.map(resource => (
              <div key={resource.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      <FileText className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-gray-800 mb-1">{resource.title}</h2>
                      <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full mb-2">
                        {resource.category}
                      </span>
                      <p className="text-gray-600 mb-3">{resource.description}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <span>{resource.type}</span>
                        <span className="mx-2">•</span>
                        <span>{resource.size}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4 border-t border-gray-100">
                  
                    <a
                    
                    href={'https://docs.google.com/document/d/1RCQMgbeA005pUlvprDKlBr9bBGDaxDibzKKBHK2AztU/edit?usp=sharinghttps://docs.google.com/document/d/1RCQMgbeA005pUlvprDKlBr9bBGDaxDibzKKBHK2AztU/edit?tab=t.0'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-center flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> Preview
                  </a>
                    
                    
                    <button
                      onClick={() => handleDownload(resource)}
                      disabled={downloading === resource.id}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-medium py-2 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-75"
                    >
                      {downloading === resource.id ? (
                        <>
                          <ArrowDownCircle className="w-4 h-4 animate-bounce" /> Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" /> Download
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">No resources found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

     
      </div>
    </div>
  );
}